// Agent session hook — manages session lifecycle, SSE streaming, reconnection,
// stuck detection, and event replay.
// Copied + parameterised from frontend/src/hooks/useAgentSession.ts.
// Endpoints, model selection, and callbacks are props/config instead of
// hard-coded Platinum routes. No dependency on AccountContext, app router,
// or Platinum constants. See ../../docs/90-provenance-map.md.

import { useCallback, useRef, useState } from 'react'
import type {
  ActivityStatus,
  AgentMessage,
  AgentMessageSearchResult,
  AgentSession,
  AgentSessionListItem,
  AgentSSEEvent,
  ArtifactInfo,
  AskUserQuestionInfo,
  CreateAgentSessionRequest,
  CreatedDashboardInfo,
  PageToolSpec,
  PersistedAgentMessage,
  RenderedChartInfo,
  RenderedTableInfo,
  TodoItem,
} from './types.js'
import { historyEventsWithoutActiveTurn, replayEvents, replayFromPersistedMessages, type QueryEventsRow } from './replayEvents.js'
import { mergeDbAndSseArtifacts } from './artifactFilters.js'
import { SSELineBuffer } from './sseLines.js'
import { agentEventReducer, initialAgentEventState, type AgentEventState } from './agentEventReducer.js'
import type { AgentChatEndpoints } from './plugins.js'
import { DEFAULT_ENDPOINTS } from './plugins.js'

/** What a page helper chat adds to a send: the page's tools and what is on screen. */
export interface PageSendOptions {
  pageTools?: PageToolSpec[]
  pageContext?: string
}

/**
 * The page options, or a function that builds them. A function is called when
 * the turn is posted, so a message queued behind a running turn describes the
 * page as it is when it goes, not as it was when it was typed (plan D4).
 */
export type PageSend = PageSendOptions | (() => PageSendOptions)

/**
 * How long a live stream may go without a single byte before it is treated as
 * dropped and reattached. The sandbox heartbeats every 15s, so this is four
 * missed heartbeats. There is deliberately NO limit on a turn's total length:
 * the client used to abort every stream at five minutes, which cut long turns
 * (a TURF run) mid-flight, gave the composer back, and let the user's next
 * message supersede the still-running turn.
 * `/reconnect` is also a live stream: it replays a resumed turn, then follows it.
 */
export const STREAM_IDLE_TIMEOUT_MS = 60_000

/**
 * How long a chat waits for a server that is AWAY (a release restarting goapi:
 * network errors, timeouts, 502/503/504 from the proxy) before it says so.
 * While it waits it checks every second, the gap growing to five, says it is
 * reconnecting, and keeps the stuck banner off. Stop and leaving the session
 * end the wait at once.
 */
export const SERVER_AWAY_WAIT_MS = 180_000

/** Shown when the server stayed away for the whole SERVER_AWAY_WAIT_MS. */
export const SERVER_AWAY_MESSAGE = 'Platinum is not answering. Your chat is saved; reload when it is back.'

/** The activity label while the chat waits for the server to come back. */
export const DEFAULT_RECONNECT_LABEL = 'Reconnecting to Platinum — your chat will carry on'

interface SessionStatusBody {
  activeQuery: { queryId: string } | null
}

/**
 * A status check's answer, or why there is none. `away`: the server did not
 * answer (a network error, the check's timeout, or a 502/503/504 from the proxy
 * in front of a restarting goapi). `gone`: it answered with any other failure.
 */
type StatusProbe = SessionStatusBody | 'away' | 'gone'

/** What a wait for the server came to: an answer, `gone`, `away` for the whole wait, or `stopped` (Stop, or the session changed). */
type ServerWaitOutcome = SessionStatusBody | 'away' | 'gone' | 'stopped'

/** The proxy's answers while goapi is down. */
function isAwayHttpStatus(status: number): boolean {
  return status === 502 || status === 503 || status === 504
}

/** fetch rejects with a TypeError when the request never reached a server. */
function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError
}

/** Resolve after `ms`, or at once when `signal` aborts. */
function sleepUnlessAborted(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal?.aborted) { resolve(); return }
    const done = () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', done)
      resolve()
    }
    const timer = setTimeout(done, ms)
    signal?.addEventListener('abort', done, { once: true })
  })
}

// Module-level poll/timer variables — survive component unmount/remount cycles.
let _containerPollTimer: ReturnType<typeof setInterval> | null = null
let _containerPollSessionId: string | null = null
const CONTAINER_POLL_MS = 2000

/**
 * Whether the process-state poll may stop. A chat's process is either running
 * or not; the poll runs on while a turn is in flight on a chat whose process
 * is not up yet (that send starts it), so the History badge turns to Ready.
 */
export function containerPollSettled(state: string | undefined, turnRunning: boolean): boolean {
  if (state === 'running') return true
  if (!state || state === 'unknown') return false
  return !turnRunning
}

export interface PersonaInfo {
  name: string
  prompt: string
  source: 'default' | 'custom'
}

export interface UseAgentSessionOptions {
  /** API base URL. Default: empty string (same origin). */
  apiBaseUrl?: string
  /** Override endpoint paths. */
  endpoints?: Partial<AgentChatEndpoints>
  /** Default model ID to use for new sessions. */
  defaultModel?: string
  /** Auth header value (e.g. "Bearer <token>"). Sent with all requests. */
  authHeader?: string
  /**
   * Async (or sync) token provider. If provided, resolves a fresh token on
   * every request and sets the Authorization header. Takes precedence over
   * `authHeader` when both are set.
   */
  getAuthToken?: () => Promise<string> | string
  /**
   * Event types that belong to registered RenderPlugins — e.g.
   * `plugins.flatMap(p => p.eventTypes)`. Events of these types are
   * accumulated in the returned `pluginEvents` array and forwarded to
   * `<AgentChat pluginEvents={...} />` so plugins can fold them per toolCallId.
   */
  pluginEventTypes?: string[]
  onToolResult?: (toolName: string, toolCallId: string, output: string, input: Record<string, unknown>) => void
  onSessionTitle?: (sessionId: string, title: string) => void
  onArtifactsUpdated?: (sessionId: string) => void
  /**
   * Called when an agent turn ends (query_complete), with the turn's status
   * ('completed', 'cancelled', 'error', …). A host refreshes what the agent
   * may have changed during the turn.
   */
  onTurnComplete?: (sessionId: string, status: string | undefined) => void
  /**
   * How long to wait between status checks that could not be answered at all
   * after a stream dropped (the server restarting). Default 1000ms; tests
   * shorten it. The gap grows to five times this while the server stays away.
   */
  statusRetryDelayMs?: number
  /**
   * The activity label while the chat waits for the server to come back.
   * Default DEFAULT_RECONNECT_LABEL; a host that knows a release is under way
   * can say so.
   */
  reconnectLabel?: string
}

interface UseAgentSessionReturn {
  session: AgentSession | null
  messages: AgentMessage[]
  isStreaming: boolean
  /** Raw plugin events (live + restored). Forward to <AgentChat pluginEvents={...} />. */
  pluginEvents: AgentSSEEvent[]
  isCreating: boolean
  error: string | null
  /** The `code` a send-turn 403 carried alongside `error` (#1537), or null. */
  errorCode: string | null
  activityStatus: ActivityStatus | null
  artifacts: ArtifactInfo[]
  todos: TodoItem[]
  renderedTables: Map<string, RenderedTableInfo>
  renderedCharts: Map<string, RenderedChartInfo>
  askedQuestions: Map<string, AskUserQuestionInfo>
  createdDashboards: Map<string, CreatedDashboardInfo>
  pendingPageToolRequest: { toolName: string; toolCallId: string } | null
  /** Calls of the page's own tools, oldest first (see AgentEventState.pageToolCalls). */
  pageToolCalls: AgentEventState['pageToolCalls']
  pendingSettingsUpdate: { toolCallId: string; settings: Record<string, unknown> } | null
  allPersonas: PersonaInfo[]
  sessionList: AgentSessionListItem[]
  hasMoreSessions: boolean
  toolInputBuffer: string
  subagentEvents: AgentEventState['subagentEvents']
  installedSkills: AgentEventState['installedSkills']
  /** The draft skill this session last distilled in this page's life (see AgentEventState.hoistedSkill). */
  hoistedSkill: AgentEventState['hoistedSkill']
  sessionJobs: AgentEventState['sessionJobs']
  /**
   * Messages sent while a turn was running, waiting for it to finish — drawn
   * below the conversation with a "Queued" marker. See sendMessage.
   */
  queuedMessages: AgentMessage[]
  /**
   * Adopt a job set the host just wrote through the session-jobs route.
   *
   * The point is that a USER-driven change (ticking a box) and an AGENT-driven
   * change (a `session_jobs_changed` event) land in the SAME state, so every
   * consumer of `sessionJobs` — the Jobs tab, the Tables tab's job picker, the
   * Variables tab's — sees both immediately. Without this the two paths behave
   * differently and a user's tick never reaches the other tabs.
   *
   * The reducer stays the only writer for events; this is the host's door in.
   */
  setSessionJobs: (jobs: string[]) => void
  selectedModel: string
  setSelectedModel: (model: string) => void
  lastHeartbeat: number
  stuckStatus: 'ok' | 'possibly_stuck' | 'likely_stuck'
  nudgeAgent: () => Promise<void>
  createSession: (req: CreateAgentSessionRequest) => Promise<string | null>
  /** `page`: the page's tools and a description of what is on screen, for a page helper chat. */
  sendMessage: (content: string, model?: string, attachmentIds?: string[], effort?: string, page?: PageSend) => Promise<void>
  /** Answer one of the agent's page tool calls. Rejects when the server refuses (the call is gone). */
  postPageToolResult: (callId: string, result: { ok: boolean; result?: unknown; error?: string }) => Promise<void>
  /** The page is holding a call (a Confirm card is up): no "quiet for a while" banner. */
  setPageCallWaiting: (waiting: boolean) => void
  cancelSession: () => Promise<void>
  deleteSession: () => Promise<void>
  loadPersonas: (customer: string, job?: string) => Promise<void>
  resumeSession: (sessionId: string) => Promise<void>
  /** `page`: the page helper filter, sent as `?page=` ('all' or a page key; absent leaves page helper chats out). */
  listSessions: (customer?: string, userEmail?: string, page?: string) => Promise<void>
  loadMoreSessions: (customer?: string, userEmail?: string, page?: string) => Promise<void>
  searchSessions: (customer: string, query: string, userEmail?: string) => Promise<AgentMessageSearchResult[]>
  clearSession: () => void
  markQuestionAnswered: (toolCallId: string, value: string) => void
  clearPendingPageToolRequest: () => void
  clearPendingSettingsUpdate: () => void
}

export default function useAgentSession(options: UseAgentSessionOptions = {}): UseAgentSessionReturn {
  const {
    apiBaseUrl = '',
    endpoints: endpointOverrides,
    defaultModel = '',
    authHeader,
    getAuthToken,
    pluginEventTypes,
    onToolResult,
    onSessionTitle,
    onArtifactsUpdated,
    onTurnComplete,
    statusRetryDelayMs = 1000,
    reconnectLabel,
  } = options
  // Read by the wait, which runs inside callbacks created on an earlier render.
  const reconnectLabelRef = useRef(reconnectLabel)
  reconnectLabelRef.current = reconnectLabel

  // Stable Set of plugin event types so handleSSEEvent closure need not depend
  // on the array identity (avoids unnecessary re-creation of the callback).
  const pluginEventTypesRef = useRef<Set<string>>(new Set(pluginEventTypes))
  // Keep in sync if the caller passes a new array (rare, but possible).
  pluginEventTypesRef.current = new Set(pluginEventTypes)

  const endpoints: AgentChatEndpoints = {
    ...DEFAULT_ENDPOINTS,
    ...endpointOverrides,
  }

  const [session, setSession] = useState<AgentSession | null>(null)
  const [messages, setMessages] = useState<AgentMessage[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // The structured `code` a send-turn 403 carried alongside `error` (#1537:
  // 'ai_access_level' or 'ai_budget'), so a host can draw the refusal as the
  // notice card rather than the generic error line. Null for every other
  // error (a stream drop, a network failure) — those are not refusals.
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const [artifacts, setArtifacts] = useState<ArtifactInfo[]>([])
  const [todos, setTodos] = useState<TodoItem[]>([])
  const [renderedTables, setRenderedTables] = useState<Map<string, RenderedTableInfo>>(new Map())
  const [renderedCharts, setRenderedCharts] = useState<Map<string, RenderedChartInfo>>(new Map())
  const [askedQuestions, setAskedQuestions] = useState<Map<string, AskUserQuestionInfo>>(new Map())
  const [createdDashboards, setCreatedDashboards] = useState<Map<string, CreatedDashboardInfo>>(new Map())
  const [pendingPageToolRequest, setPendingPageToolRequest] = useState<{ toolName: string; toolCallId: string } | null>(null)
  const [pageToolCalls, setPageToolCalls] = useState<AgentEventState['pageToolCalls']>([])
  const [pendingSettingsUpdate, setPendingSettingsUpdate] = useState<{ toolCallId: string; settings: Record<string, unknown> } | null>(null)
  // The session's job set, from session_jobs_changed. Held in React state, not
  // read off the ref: a ref change does not re-render, so the Jobs tab would
  // only update when some neighbouring event happened to force a paint.
  const [sessionJobs, setSessionJobs] = useState<AgentEventState['sessionJobs']>(null)
  const [queuedMessages, setQueuedMessages] = useState<AgentMessage[]>([])
  const [allPersonas, setAllPersonas] = useState<PersonaInfo[]>([])
  const [sessionList, setSessionList] = useState<AgentSessionListItem[]>([])
  const [hasMoreSessions, setHasMoreSessions] = useState(false)
  const [selectedModel, setSelectedModel] = useState<string>(defaultModel)
  // The model offering is fetched from the server, so defaultModel is empty on
  // the first render and arrives a tick later. useState captured that empty
  // string and would never revisit it, leaving the picker with no selection —
  // it then displayed the first model in the list and would have sent the next
  // message as that model rather than the chosen one.
  //
  // Adopted in render rather than an effect (this hook runs none): the guard is
  // self-clearing, because once a selection exists the condition is false. That
  // also means a choice already made — by the user, or by a resumed session —
  // is never overwritten.
  if (defaultModel && !selectedModel) setSelectedModel(defaultModel)
  // Read by resumeSession, which must not re-run when the selection changes.
  const selectedModelRef = useRef(selectedModel)
  selectedModelRef.current = selectedModel
  const [activityStatus, setActivityStatus] = useState<ActivityStatus | null>(null)
  const [toolInputBuffer, setToolInputBuffer] = useState<string>('')
  const [stuckStatus, setStuckStatus] = useState<'ok' | 'possibly_stuck' | 'likely_stuck'>('ok')
  const abortControllerRef = useRef<AbortController | null>(null)
  const sessionIdRef = useRef<string | null>(null)
  const lastHeartbeatRef = useRef<number>(Date.now())
  const lastEventTimeRef = useRef<number>(Date.now())
  const stuckTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pageCallWaitingRef = useRef(false)
  const streamHeldRef = useRef(false)

  // Plugin side-channel: raw events whose types belong to registered plugins.
  // Kept separately from core reducer state so AgentChat can fold them per-toolCallId.
  const [pluginEvents, setPluginEventsState] = useState<AgentSSEEvent[]>([])
  // Mirror of pluginEvents, so a reconnect can rewind to the turn's start
  // without waiting for a render (see turnBaseRef).
  const pluginEventsRef = useRef<AgentSSEEvent[]>([])
  const setPluginEvents = (next: AgentSSEEvent[]) => {
    pluginEventsRef.current = next
    setPluginEventsState(next)
  }

  // Pure reducer state — single source of truth for SSE event processing.
  const eventStateRef = useRef<AgentEventState>(initialAgentEventState())

  // The state as it stood when the running turn began: the conversation before
  // it plus the user's own bubble. A reconnect stream replays the active turn
  // from its FIRST event (the W1/W2 handshake), so before reading one the state
  // is rewound to here — otherwise every delta the client already had would be
  // applied twice ("abcabcdef").
  const turnBaseRef = useRef<{ state: AgentEventState; pluginEvents: AgentSSEEvent[] } | null>(null)
  // Content of the user bubble already on screen for the active turn. If the
  // reconnect replay also carries that turn's user_message, it is dropped once
  // rather than drawn as a second bubble.
  const replayUserDedupeRef = useRef<string | null>(null)

  // --- The send queue (W1/W2 handshake item 3) -----------------------------
  // A prompt POSTed while a turn is running makes the sandbox ABORT that turn,
  // which is how "Are you stuck?" killed a six-minute TURF run. So a message
  // sent mid-turn is held here and POSTed when the turn ends. The reducer draws
  // it (user_message_queued / queued_message_sent); this ref holds what is
  // needed to actually send it.
  const queueRef = useRef<Array<{ id: string; content: string; model?: string; attachmentIds?: string[]; effort?: string; page?: PageSend }>>([])
  // True from a turn's POST (or a reload's reattach) until its stream is done.
  const turnActiveRef = useRef(false)
  // Bumped per turn, so a stale turn's cleanup cannot clear a newer turn's flag.
  const turnSeqRef = useRef(0)

  /** Put a whole reducer state on screen (a rewind or a restore, not a delta). */
  const commitState = (s: AgentEventState) => {
    eventStateRef.current = s
    setMessages(s.messages)
    setIsStreaming(s.isStreaming)
    setError(s.error)
    // The reducer's own error field is a mid-stream 'error' frame, never a
    // pre-stream refusal (those never reach the reducer — see the pitfall in
    // CLAUDE.md: "add no mid-stream check"), so it never carries a code.
    setErrorCode(null)
    setActivityStatus(s.activityStatus)
    setArtifacts(s.artifacts)
    setTodos(s.todos)
    setRenderedTables(s.renderedTables)
    setRenderedCharts(s.renderedCharts)
    setAskedQuestions(s.askedQuestions)
    setCreatedDashboards(s.createdDashboards)
    setPendingPageToolRequest(s.pendingPageToolRequest)
    setPageToolCalls(s.pageToolCalls)
    setPendingSettingsUpdate(s.pendingSettingsUpdate)
    setSessionJobs(s.sessionJobs)
    setToolInputBuffer(s.toolInputBuffer)
    setQueuedMessages(s.queuedMessages)
  }

  /** Remember the current state as the start of the running turn. */
  const markTurnBase = () => {
    turnBaseRef.current = { state: eventStateRef.current, pluginEvents: pluginEventsRef.current }
  }

  /**
   * Rewind to the start of the running turn before a reconnect replays it.
   * Session-wide things that replay idempotently (the artifact list, the job
   * set, installed skills) keep their current values: a DB artifact load that
   * landed mid-turn must not be thrown away.
   */
  const rewindToTurnBase = () => {
    const base = turnBaseRef.current
    if (!base) return
    const cur = eventStateRef.current
    commitState({
      ...base.state,
      isStreaming: true,
      error: null,
      activityStatus: { label: 'Reconnecting...', category: 'system' },
      artifacts: cur.artifacts,
      sessionJobs: cur.sessionJobs,
      installedSkills: cur.installedSkills,
      queuedMessages: cur.queuedMessages,
    })
    setPluginEvents(base.pluginEvents)
    // Only when the base ENDS in this turn's bubble (the optimistic one, or a
    // persisted leading prompt). After a reload the active turn has no row, so
    // the base ends with the previous turn's answer, and the replayed prompt
    // must be drawn even if it repeats an earlier one word for word.
    const last = base.state.messages[base.state.messages.length - 1]
    replayUserDedupeRef.current = last?.role === 'user' ? last.content : null
  }

  async function buildHeadersAsync(extra?: Record<string, string>): Promise<Record<string, string>> {
    const h: Record<string, string> = { ...extra }
    if (getAuthToken) {
      const token = await getAuthToken()
      if (token) h['Authorization'] = `Bearer ${token}`
    } else if (authHeader) {
      h['Authorization'] = authHeader
    }
    return h
  }

  async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
    const headers = await buildHeadersAsync(init?.headers as Record<string, string> | undefined)
    return fetch(apiBaseUrl + url, {
      ...init,
      headers,
    })
  }

  async function apiGet<T>(url: string): Promise<T> {
    const resp = await apiFetch(url)
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    return resp.json() as Promise<T>
  }

  const loadPersonas = useCallback(async (customer: string, job?: string) => {
    try {
      const url = job ? `/personas/${customer}?job=${encodeURIComponent(job)}` : `/personas/${customer}`
      const data = await apiGet<{ personas: PersonaInfo[] }>(url)
      setAllPersonas(data?.personas || [])
    } catch (err) {
      console.error('Failed to load personas:', err)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authHeader, getAuthToken])

  const loadArtifacts = useCallback(async (sessionId: string) => {
    try {
      const data = await apiGet<{ artifacts?: Record<string, unknown>[] } | Record<string, unknown>[]>(
        endpoints.artifacts(sessionId)
      )
      // The artifacts endpoint returns a bare JSON array; some deployments wrap
      // it as { artifacts: [...] }. Accept both so the DB-backed artifacts (which
      // carry the `id` used for downloads) are not silently dropped.
      const rows: Record<string, unknown>[] = Array.isArray(data)
        ? data
        : (data.artifacts || [])
      // API returns snake_case JSON — normalize to camelCase ArtifactInfo
      const loaded: ArtifactInfo[] = rows.map((a: Record<string, unknown>) => {
        const filePath = (a.file_path as string) || (a.filePath as string) || ''
        return {
        id: (a.id as string) || undefined,
        filePath,
        fileName: (a.file_name as string) || (a.fileName as string) || filePath.split('/').pop() || '',
        fileSize: (a.file_size as number | undefined) ?? (a.fileSize as number | undefined),
        mimeType: (a.mime_type as string) || (a.mimeType as string) || undefined,
        label: (a.label as string) || '',
        description: (a.description as string) || undefined,
        artifactType: ((a.artifact_type || a.artifactType) as ArtifactInfo['artifactType']) || 'file',
        source: ((a.source) as ArtifactInfo['source']) || 'auto',
        status: ((a.status) as ArtifactInfo['status']) || 'live',
        isDir: (a.is_dir as boolean) ?? (a.isDir as boolean) ?? false,
        updatedAt: (a.updated_at as number | undefined) ?? (a.updatedAt as number | undefined),
        }
      })
      // Merge: keep SSE-registered artifacts the DB doesn't know about yet, and
      // reconcile webapp entry paths against their captured dir rows (status/id
      // adoption) — see mergeDbAndSseArtifacts.
      const merged = mergeDbAndSseArtifacts(loaded, eventStateRef.current.artifacts)
      eventStateRef.current = { ...eventStateRef.current, artifacts: merged }
      setArtifacts(merged)
    } catch (err) {
      console.error('Failed to load artifacts:', err)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authHeader, getAuthToken])

  /**
   * Dispatch an SSE event through the pure reducer, then sync changed fields
   * to React state. Side effects (session updates, artifact loading, heartbeat)
   * are handled inline after the reducer runs.
   */
  const handleSSEEvent = useCallback((event: AgentSSEEvent) => {
    lastHeartbeatRef.current = Date.now()

    const progressTypes = ['content_delta', 'thinking_delta', 'tool_use_start', 'tool_use_end',
      'tool_progress', 'activity_update', 'message_start', 'system_status',
      'tool_input_delta', 'subagent_event', 'workflow_event']
    if (progressTypes.includes(event.type)) {
      lastEventTimeRef.current = Date.now()
    }
    const prevState = eventStateRef.current
    const nextState = agentEventReducer(prevState, event)
    eventStateRef.current = nextState

    // Sync only changed fields to React state (referential equality check)
    if (nextState.messages !== prevState.messages) setMessages(nextState.messages)
    if (nextState.isStreaming !== prevState.isStreaming) setIsStreaming(nextState.isStreaming)
    // A reducer-driven error is a mid-stream 'error' frame, never a code-carrying
    // pre-stream refusal — see the commitState comment above.
    if (nextState.error !== prevState.error) { setError(nextState.error); setErrorCode(null) }
    if (nextState.activityStatus !== prevState.activityStatus) setActivityStatus(nextState.activityStatus)
    if (nextState.artifacts !== prevState.artifacts) setArtifacts(nextState.artifacts)
    if (nextState.todos !== prevState.todos) setTodos(nextState.todos)
    if (nextState.renderedTables !== prevState.renderedTables) setRenderedTables(nextState.renderedTables)
    if (nextState.renderedCharts !== prevState.renderedCharts) setRenderedCharts(nextState.renderedCharts)
    if (nextState.askedQuestions !== prevState.askedQuestions) setAskedQuestions(nextState.askedQuestions)
    if (nextState.createdDashboards !== prevState.createdDashboards) setCreatedDashboards(nextState.createdDashboards)
    if (nextState.pendingPageToolRequest !== prevState.pendingPageToolRequest) setPendingPageToolRequest(nextState.pendingPageToolRequest)
    if (nextState.pageToolCalls !== prevState.pageToolCalls) setPageToolCalls(nextState.pageToolCalls)
    if (nextState.pendingSettingsUpdate !== prevState.pendingSettingsUpdate) setPendingSettingsUpdate(nextState.pendingSettingsUpdate)
    if (nextState.sessionJobs !== prevState.sessionJobs) setSessionJobs(nextState.sessionJobs)
    if (nextState.queuedMessages !== prevState.queuedMessages) setQueuedMessages(nextState.queuedMessages)

    if (nextState.toolInputBuffer !== prevState.toolInputBuffer) {
      setToolInputBuffer(nextState.toolInputBuffer)
    }
    if (prevState.activityStatus?.category === 'preparing_tool' && nextState.activityStatus?.category !== 'preparing_tool') {
      setToolInputBuffer('')
    }

    // Accumulate plugin events in the side-channel so AgentChat can fold them
    // per-toolCallId. Reading via ref keeps this closure stable.
    if (pluginEventTypesRef.current.size > 0 && pluginEventTypesRef.current.has(event.type)) {
      setPluginEvents([...pluginEventsRef.current, event])
    }

    // --- Side effects the reducer can't handle ---

    if (event.type === 'tool_use_end' && onToolResult) {
      const eventData = event.data as Record<string, unknown>
      const tc = nextState.toolCalls.get(eventData.toolCallId as string)
      if (tc && tc.output) {
        onToolResult(tc.name, tc.id, tc.output, tc.input)
      }
    }

    if (event.type === 'session_title') {
      const { title, sessionId } = event.data as { title: string; sessionId: string }
      setSession(prev => prev?.id === sessionId ? { ...prev, title } : prev)
      setSessionList(prev => prev.map(s => s.id === sessionId ? { ...s, title } : s))
      if (onSessionTitle) onSessionTitle(sessionId, title)
    }

    // artifact_registered too: register_artifact emits only that event, so
    // without it a web app registered mid-turn stays invisible until the turn
    // ends (session bprsky/f0f3bb3d: two minutes with no page on screen).
    if (event.type === 'artifacts_updated' || event.type === 'artifact_registered') {
      if (sessionIdRef.current) {
        loadArtifacts(sessionIdRef.current)
        if (onArtifactsUpdated) onArtifactsUpdated(sessionIdRef.current)
      }
    }

    if (event.type === 'query_complete') {
      const queryStatus = (event.data as Record<string, unknown>)?.status
      if (queryStatus !== 'cancelled') {
        setSession(prev => prev ? { ...prev, status: 'completed' } : prev)
      }
      if (sessionIdRef.current) loadArtifacts(sessionIdRef.current)
      if (onTurnComplete && sessionIdRef.current) {
        onTurnComplete(sessionIdRef.current, typeof queryStatus === 'string' ? queryStatus : undefined)
      }

      // Delayed title refresh fallback
      const sid = sessionIdRef.current
      if (sid) {
        setTimeout(async () => {
          try {
            const s = await apiGet<{ title?: string; id: string }>(
              endpoints.getSession(sid)
            )
            if (s.title) {
              setSession(prev => prev?.id === sid ? { ...prev, title: s.title } : prev)
              setSessionList(prev => prev.map(item => item.id === sid ? { ...item, title: s.title! } : item))
            }
          } catch { /* best effort */ }
        }, 5000)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadArtifacts, onToolResult, onSessionTitle, onArtifactsUpdated, onTurnComplete])

  const markQuestionAnswered = useCallback((toolCallId: string, value: string) => {
    const nextQuestions = new Map(eventStateRef.current.askedQuestions)
    const q = nextQuestions.get(toolCallId)
    if (q) nextQuestions.set(toolCallId, { ...q, answered: true, selectedValue: value })
    eventStateRef.current = { ...eventStateRef.current, askedQuestions: nextQuestions }
    setAskedQuestions(nextQuestions)
  }, [])

  const clearPendingPageToolRequest = useCallback(() => {
    eventStateRef.current = { ...eventStateRef.current, pendingPageToolRequest: null }
    setPendingPageToolRequest(null)
  }, [])

  const clearPendingSettingsUpdate = useCallback(() => {
    eventStateRef.current = { ...eventStateRef.current, pendingSettingsUpdate: null }
    setPendingSettingsUpdate(null)
  }, [])

  const resetEventState = () => {
    eventStateRef.current = initialAgentEventState()
  }

  function startStuckDetection() {
    lastEventTimeRef.current = Date.now()
    lastHeartbeatRef.current = Date.now()
    setStuckStatus('ok')
    if (stuckTimerRef.current) clearInterval(stuckTimerRef.current)
    stuckTimerRef.current = setInterval(() => {
      // The agent is waiting on the person's Confirm click, which is not a stall.
      if (pageCallWaitingRef.current) {
        lastEventTimeRef.current = Date.now()
        lastHeartbeatRef.current = Date.now()
        setStuckStatus('ok')
        return
      }
      const sinceHeartbeat = Date.now() - lastHeartbeatRef.current
      const sinceProgress = Date.now() - lastEventTimeRef.current
      if (streamHeldRef.current && sinceHeartbeat > 45_000) setStuckStatus('likely_stuck')
      else if (sinceProgress > 120_000) setStuckStatus('likely_stuck')
      else if (sinceProgress > 60_000) setStuckStatus('possibly_stuck')
      else setStuckStatus('ok')
    }, 5_000)
  }

  function stopStuckDetection() {
    if (stuckTimerRef.current) {
      clearInterval(stuckTimerRef.current)
      stuckTimerRef.current = null
    }
    setStuckStatus('ok')
  }

  const createSession = useCallback(async (req: CreateAgentSessionRequest): Promise<string | null> => {
    clearQueue()
    turnSeqRef.current++
    turnActiveRef.current = false
    resetEventState()
    setError(null)
    setErrorCode(null)
    setMessages([])
    setArtifacts([])
    setRenderedTables(new Map())
    setRenderedCharts(new Map())
    setAskedQuestions(new Map())
    setCreatedDashboards(new Map())
    setPluginEvents([])
    setIsCreating(true)
    try {
      const response = await apiFetch(endpoints.createSession, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(req),
      })
      if (!response.ok) {
        // The host's own refusal (an access check, say) reads better than a
        // status code, the same as on send.
        const text = await response.text()
        let message = `HTTP ${response.status}`
        try { message = (JSON.parse(text) as { error?: string }).error || message } catch { /* use default */ }
        throw new Error(message)
      }
      const data = await response.json() as { id: string; status: string; workflowId: string }
      const newSession: AgentSession = {
        id: data.id,
        status: 'active',
        workflowId: req.workflow_id || 'agent',
        persona: req.persona,
        customer: req.customer,
        job: req.job,
        jobs: req.jobs,
      }
      setSession(newSession)
      sessionIdRef.current = newSession.id
      // Insert the new session into the list (if absent) so the process-state
      // poll has a row to update.
      setSessionList(prev => {
        if (prev.some(s => s.id === newSession.id)) return prev
        const now = Math.floor(Date.now() / 1000)
        const row: AgentSessionListItem = {
          id: newSession.id,
          created_at: now,
          updated_at: now,
          user_email: '',
          customer: req.customer ?? '',
          job: req.job ?? '',
          workflow_id: req.workflow_id || 'agent',
          persona: req.persona ?? '',
          status: 'active',
          current_node: '',
          title: '',
          artifact_count: 0,
        }
        return [row, ...prev]
      })
      startContainerStatePoll(newSession.id)
      return newSession.id
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to create session')
      setErrorCode(null)
      return null
    } finally {
      setIsCreating(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authHeader, getAuthToken])

  /** Max reconnection attempts before falling back to DB load */
  const MAX_RECONNECT_ATTEMPTS = 3

  /**
   * Ask the server for the session's status. The 5s timeout counts as `away`:
   * right after a goapi restart the status route may block on the orphaned
   * turn check for longer than that.
   */
  const probeSessionStatus = async (sessionId: string): Promise<StatusProbe> => {
    let resp: Response
    try {
      resp = await apiFetch(
        endpoints.status(sessionId),
        { signal: AbortSignal.timeout(5000) }
      )
    } catch {
      return 'away'
    }
    if (!resp.ok) return isAwayHttpStatus(resp.status) ? 'away' : 'gone'
    try {
      const body = await resp.json() as SessionStatusBody | null
      // A 200 that is not JSON is a proxy's page, not the status route.
      return body && typeof body === 'object' ? body : 'away'
    } catch {
      return 'away'
    }
  }

  const checkSessionStatus = async (sessionId: string): Promise<SessionStatusBody | null> => {
    const probe = await probeSessionStatus(sessionId)
    return typeof probe === 'object' ? probe : null
  }

  // The queryId is what tells the server WHICH turn to replay; without it the
  // runner built /stream/ with an empty id and the reconnect found nothing.
  const attemptReconnect = async (sessionId: string, queryId: string): Promise<Response | null> => {
    try {
      const resp = await apiFetch(
        `${endpoints.reconnect(sessionId)}?queryId=${encodeURIComponent(queryId)}`,
        // Stop aborts it; nothing else does (see STREAM_IDLE_TIMEOUT_MS).
        { signal: abortControllerRef.current?.signal }
      )
      if (resp.ok && resp.headers.get('content-type')?.includes('text/event-stream')) {
        return resp
      }
    } catch {
      // Reconnect failed
    }
    return null
  }

  /** Show an activity label (or none), in the reducer's state as well as React's. */
  const showActivity = (status: ActivityStatus | null) => {
    eventStateRef.current = { ...eventStateRef.current, activityStatus: status }
    setActivityStatus(status)
  }

  /** Show an error that is not a refusal (no code). */
  const showError = (message: string) => {
    eventStateRef.current = { ...eventStateRef.current, error: message }
    setError(message)
    setErrorCode(null)
  }

  /**
   * Waiting for the server is not the agent going quiet: move the clocks the
   * stuck detector reads, so the "quiet for a while" banner stays off.
   */
  const holdStuckDetection = () => {
    lastEventTimeRef.current = Date.now()
    lastHeartbeatRef.current = Date.now()
  }

  /**
   * The session's status after a stream dropped or a send failed. Both are
   * often the server itself going away (a release, a dev hot reload), and for
   * a while nothing answers — which is not the same as "no turn is running".
   * Keep asking while the server is AWAY, for up to SERVER_AWAY_WAIT_MS; stop
   * at the first answer, at `gone`, at Stop, or when the session changes.
   */
  const statusAfterDrop = async (sessionId: string): Promise<ServerWaitOutcome> => {
    const signal = abortControllerRef.current?.signal
    const deadline = Date.now() + SERVER_AWAY_WAIT_MS
    let delay = statusRetryDelayMs
    let waiting = false
    while (true) {
      const probe = await probeSessionStatus(sessionId)
      if (sessionIdRef.current !== sessionId || signal?.aborted) return 'stopped'
      if (probe !== 'away') return probe
      if (Date.now() >= deadline) return 'away'
      if (!waiting) {
        waiting = true
        showActivity({ label: reconnectLabelRef.current ?? DEFAULT_RECONNECT_LABEL, category: 'system' })
        setStuckStatus('ok')
      }
      holdStuckDetection()
      await sleepUnlessAborted(Math.min(delay, Math.max(0, deadline - Date.now())), signal)
      holdStuckDetection()
      if (sessionIdRef.current !== sessionId || signal?.aborted) return 'stopped'
      delay = Math.min(delay + statusRetryDelayMs, statusRetryDelayMs * 5)
    }
  }

  /**
   * Load the whole conversation from what the server persisted — every turn,
   * the active one included. The fallback when there is no live stream to read.
   */
  const loadPersistedState = async (sessionId: string) => {
    loadArtifacts(sessionId)
    try {
      let restored: AgentEventState | null = null
      let restoredFromRawEvents = false
      try {
        const eventsData = await apiGet<{ events: QueryEventsRow[] }>(endpoints.queryEvents(sessionId))
        const queryEvents = eventsData.events || []
        if (queryEvents.length > 0) {
          const allEvents = historyEventsWithoutActiveTurn(queryEvents, null)
          restored = replayEvents(allEvents)
          // Restore plugin events from raw events so plugins can reconstruct state.
          const typeSet = pluginEventTypesRef.current
          setPluginEvents(typeSet.size > 0 ? allEvents.filter(e => typeSet.has(e.type)) : [])
          restoredFromRawEvents = true
        }
      } catch { /* fall through to legacy */ }

      if (!restored) {
        const msgsData = await apiGet<{ messages: PersistedAgentMessage[]; total: number }>(
          `${endpoints.messages(sessionId)}?limit=500`
        )
        const persisted = msgsData.messages || []
        if (persisted.length > 0) {
          restored = replayFromPersistedMessages(persisted)
        }
      }

      if (restored) {
        eventStateRef.current = {
          ...eventStateRef.current,
          messages: restored.messages,
          renderedTables: restored.renderedTables,
          renderedCharts: restored.renderedCharts,
          askedQuestions: restored.askedQuestions,
          createdDashboards: restored.createdDashboards,
          todos: restored.todos,
          sessionInfo: restored.sessionInfo,
          subagentEvents: restored.subagentEvents,
          artifacts: restored.artifacts,
          installedSkills: restored.installedSkills,
          sessionJobs: restored.sessionJobs,
        }
        setMessages(restored.messages)
        setRenderedTables(restored.renderedTables)
        setRenderedCharts(restored.renderedCharts)
        setAskedQuestions(restored.askedQuestions)
        setCreatedDashboards(restored.createdDashboards)
        setTodos(restored.todos)
        setArtifacts(restored.artifacts)
        setSessionJobs(restored.sessionJobs)
        // If we fell back to persisted messages, plugin state cannot be
        // reconstructed — reset to empty (renders nothing; live events will fill it).
        if (!restoredFromRawEvents) setPluginEvents([])
      }
    } catch { /* best effort */ }
  }

  /**
   * Read a turn's SSE stream to its end, reattaching if it drops mid-turn.
   * Resolves true when the turn is known to be over (query_complete arrived,
   * or the server reports no active query); false when that is unknown — the
   * stream was lost and could not be reattached. The send queue waits for the
   * server in the second case rather than supersede a turn still running.
   */
  const readSSEStream = async (
    response: Response,
    reconnectDepth = 0,
    expectedSessionId?: string,
    /** A reconnect replay (GET …/reconnect), not the POST that started the turn. */
    isReplay = reconnectDepth > 0,
  ): Promise<boolean> => {
    const reader = response.body!.getReader()
    streamHeldRef.current = true
    lastHeartbeatRef.current = Date.now()
    lastEventTimeRef.current = Date.now()
    const decoder = new TextDecoder()
    // Linear in the stream's length, however the network chunks it (see sseLines.ts).
    const sseLines = new SSELineBuffer()
    let receivedQueryComplete = false
    // The host writes `event: error` frames (data: {"message": …}) when the
    // runner fails — for a reconnect, "status 404" when the sandbox no longer
    // knows the turn. Their data carries no `type`, so without tracking the
    // frame's event name they were silently dropped.
    let frameEvent = ''
    let errorFrame: string | null = null

    const handleLine = (line: string) => {
      if (line === '' || line === '\r') { frameEvent = ''; return }
      if (line.startsWith('event:')) { frameEvent = line.slice(6).trim(); return }
      if (!line.startsWith('data: ')) return
      let parsed: unknown
      try {
        parsed = JSON.parse(line.slice(6))
      } catch {
        return // Ignore malformed SSE data
      }
      if (frameEvent === 'error') {
        const message = (parsed as { message?: unknown })?.message
        errorFrame = typeof message === 'string' && message ? message : 'The agent stream failed'
        return
      }
      dispatch(parsed as AgentSSEEvent)
    }

    // Idle watchdog: a stream that has sent nothing — not even a heartbeat —
    // for STREAM_IDLE_TIMEOUT_MS is dead. Cancelling the reader ends the read
    // loop as if the server had closed it, which takes the ordinary "ended
    // without query_complete" path below: check for the active turn, reattach.
    let idleTimer: ReturnType<typeof setTimeout> | null = null
    const armIdle = () => {
      if (idleTimer) clearTimeout(idleTimer)
      idleTimer = setTimeout(() => { reader.cancel().catch(() => {}) }, STREAM_IDLE_TIMEOUT_MS)
    }

    const dispatch = (event: AgentSSEEvent) => {
      if (event.type === 'query_complete' || event.type === 'dag_complete') {
        receivedQueryComplete = true
      }
      // A replayed turn may open with the user_message whose bubble is already
      // on screen (the optimistic one, or the persisted leading event). Drop it
      // once; any later event ends the window.
      const dedupe = replayUserDedupeRef.current
      if (dedupe !== null) {
        if (event.type === 'user_message') {
          replayUserDedupeRef.current = null
          if (((event.data as Record<string, unknown>)?.content ?? '') === dedupe) return
        } else if (event.type !== 'heartbeat') {
          replayUserDedupeRef.current = null
        }
      }
      handleSSEEvent(event)
    }

    try {
      while (true) {
        if (expectedSessionId && sessionIdRef.current !== expectedSessionId) {
          console.log(`[SSE] Session changed (${expectedSessionId} → ${sessionIdRef.current}) — dropping stream`)
          break
        }

        armIdle()
        let chunk: ReadableStreamReadResult<Uint8Array>
        try {
          chunk = await reader.read()
        } catch (err) {
          // Stop aborts the fetch: that is the reader's choice, not a drop.
          if ((err as Error)?.name === 'AbortError' || abortControllerRef.current?.signal.aborted) throw err
          // The connection broke (goapi killed mid-stream). That is a stream
          // that ended without query_complete: check the turn and reattach.
          console.log('[SSE] Stream broke — treating it as dropped', err)
          break
        }
        const { done, value } = chunk
        if (done) break

        for (const line of sseLines.push(decoder.decode(value, { stream: true }))) handleLine(line)
      }

      if (idleTimer) { clearTimeout(idleTimer); idleTimer = null }

      const lastLine = sseLines.flush()
      if (lastLine) handleLine(lastLine)

      // A closed stream is no longer evidence that the agent missed a
      // heartbeat. Reconnection may spend time checking status and opening the
      // replacement stream; the heartbeat clock resumes when that reader is held.
      streamHeldRef.current = false

      if (errorFrame !== null && !receivedQueryComplete) {
        if (isReplay) {
          // The server has no live turn to replay (it ended, and the sandbox
          // has forgotten it). The turn is over, so its persisted row is what
          // to show — no banner, no retry loop.
          console.log(`[SSE] Reconnect refused (${errorFrame}) — the turn is over, loading persisted state`)
          if (sessionIdRef.current && (!expectedSessionId || sessionIdRef.current === expectedSessionId)) {
            await loadPersistedState(sessionIdRef.current)
          }
          return true
        }
        // The turn's own stream failed: say so, through the reducer.
        dispatch({ type: 'error', data: { message: errorFrame }, timestamp: new Date().toISOString() })
      }

      let settled = receivedQueryComplete
      if (!receivedQueryComplete && sessionIdRef.current && (!expectedSessionId || sessionIdRef.current === expectedSessionId)) {
        if (reconnectDepth < MAX_RECONNECT_ATTEMPTS) {
          console.log(`[SSE] Stream ended without query_complete — checking for active query (attempt ${reconnectDepth + 1}/${MAX_RECONNECT_ATTEMPTS})`)
          const sid = sessionIdRef.current
          const status = await statusAfterDrop(sid)
          // Stop, or the user left the session: whoever did that decides
          // what is shown next.
          if (status === 'stopped') return false
          if (status === 'away') {
            // The server stayed away for the whole wait. Its history is out of
            // reach too, so say so rather than try to load it. The queue waits
            // for the server rather than supersede a turn that may be running.
            console.warn('[SSE] Server away for the whole wait — giving up')
            showError(SERVER_AWAY_MESSAGE)
            return false
          }
          if (status !== 'gone' && status.activeQuery) {
            console.log(`[SSE] Active query found (${status.activeQuery.queryId}) — reconnecting`)
            const reconnectResp = await attemptReconnect(sid, status.activeQuery.queryId)
            if (reconnectResp) {
              // The replay starts at the turn's first event: rewind first.
              rewindToTurnBase()
              return await readSSEStream(reconnectResp, reconnectDepth + 1, expectedSessionId)
            }
            console.warn('[SSE] Reconnect failed — falling back to persisted messages')
          } else if (status !== 'gone') {
            console.log('[SSE] No active query — query may have completed, loading persisted state')
            settled = true
          } else {
            // Still unknown: the queue waits for the server rather than
            // supersede a turn that may be running.
            console.warn('[SSE] Status refused — loading persisted state')
          }
        } else {
          console.warn(`[SSE] Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached — falling back to persisted messages`)
        }

        if (sessionIdRef.current) await loadPersistedState(sessionIdRef.current)

        if (reconnectDepth >= MAX_RECONNECT_ATTEMPTS) {
          setError('Connection lost — loaded conversation from history')
          setErrorCode(null)
        }
      }
      return settled
    } finally {
      if (idleTimer) clearTimeout(idleTimer)
      streamHeldRef.current = false
      reader.releaseLock()
    }
  }

  /** How often the queue asks the server whether a lost turn has finished. */
  const QUEUE_IDLE_POLL_MS = 3000

  /**
   * Resolve true once the server reports no running turn (false if the
   * session changed or the queue emptied meanwhile). Used only after a stream
   * was lost: POSTing while the server still runs the turn would abort it.
   */
  const waitUntilServerIdle = async (sid: string): Promise<boolean> => {
    while (sessionIdRef.current === sid && queueRef.current.length > 0) {
      const status = await checkSessionStatus(sid)
      if (sessionIdRef.current !== sid || queueRef.current.length === 0) return false
      if (!status?.activeQuery) return true
      await new Promise(r => setTimeout(r, QUEUE_IDLE_POLL_MS))
    }
    return false
  }

  /**
   * Send the oldest queued message, if nothing is running. `settled` says
   * whether the turn that just ended is known to be over; if not, wait for the
   * server first.
   */
  const drainQueue = async (sid: string, settled: boolean) => {
    if (queueRef.current.length === 0) return
    if (sessionIdRef.current !== sid || turnActiveRef.current) return
    if (!settled && !(await waitUntilServerIdle(sid))) return
    if (sessionIdRef.current !== sid || turnActiveRef.current) return
    const next = queueRef.current.shift()
    if (!next) return
    // Not awaited: this turn belongs to the queue, not to whichever caller's
    // turn happened to end.
    void runTurn(sid, next.content, next.model, next.attachmentIds, next.id, next.effort, next.page)
  }

  /** Drop everything queued (a new, cleared, resumed or deleted session). */
  const clearQueue = () => {
    queueRef.current = []
    eventStateRef.current = { ...eventStateRef.current, queuedMessages: [] }
    setQueuedMessages([])
  }

  /**
   * One turn: POST the prompt and read its stream to the end. `queuedId` names
   * a queued message being released — its bubble is already drawn, so the
   * reducer moves it into the conversation instead of a second one being added.
   */
  const runTurn = async (sid: string, content: string, model?: string, attachmentIds?: string[], queuedId?: string, effort?: string, pageSend?: PageSend) => {
    const page = typeof pageSend === 'function' ? pageSend() : pageSend
    const turn = ++turnSeqRef.current
    turnActiveRef.current = true

    // Mark any unanswered questions as answered (user typed instead of clicking)
    const prevQuestions = eventStateRef.current.askedQuestions
    let questionsChanged = false
    const nextQuestions = new Map(prevQuestions)
    for (const [id, q] of nextQuestions) {
      if (!q.answered) {
        nextQuestions.set(id, { ...q, answered: true, selectedValue: content })
        questionsChanged = true
      }
    }
    if (questionsChanged) {
      eventStateRef.current = { ...eventStateRef.current, askedQuestions: nextQuestions }
      setAskedQuestions(nextQuestions)
    }

    setIsStreaming(true)
    setError(null)
    setErrorCode(null)
    eventStateRef.current = { ...eventStateRef.current, isStreaming: true, error: null }
    startStuckDetection()

    if (queuedId) {
      handleSSEEvent({ type: 'queued_message_sent', data: { id: queuedId }, timestamp: new Date().toISOString() })
    } else {
      const userMessage: AgentMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content,
        timestamp: new Date().toISOString(),
      }
      const messagesWithUser = [...eventStateRef.current.messages, userMessage]
      eventStateRef.current = { ...eventStateRef.current, messages: messagesWithUser }
      setMessages(messagesWithUser)
    }
    markTurnBase()
    // A send to a chat whose process is not running starts it; watch for
    // that so the History badge follows. The poll stops on its first look
    // when the process is already up.
    if (_containerPollSessionId !== sid) startContainerStatePoll(sid)

    const abortController = new AbortController()
    abortControllerRef.current = abortController
    let settled = false

    const post = () => apiFetch(endpoints.sendMessage(sid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content, ...(model && { model }), ...(attachmentIds?.length && { attachmentIds }), ...(effort && { effort }), ...(page?.pageTools && { pageTools: page.pageTools }), ...(page?.pageContext && { pageContext: page.pageContext }) }),
      // Stop aborts it; nothing else does (see STREAM_IDLE_TIMEOUT_MS).
      signal: abortController.signal,
    })

    /** Why a non-ok send failed, and its refusal code (a 403's only). */
    const sendFailure = async (response: Response) => {
      const text = await response.text()
      let message = `Request failed (${response.status})`
      let code: string | null = null
      try {
        const parsed = JSON.parse(text) as { error?: string; code?: string }
        message = parsed.error || message
        // Only a 403 is a refusal; a 500 that happens to carry a code is not.
        code = response.status === 403 && typeof parsed.code === 'string' && parsed.code ? parsed.code : null
      } catch { /* use default */ }
      return { message, code }
    }

    /** End the turn with an error line. */
    const failTurn = (message: string, code: string | null) => {
      setError(message)
      setErrorCode(code)
      setIsStreaming(false)
      setActivityStatus(null)
      stopStuckDetection()
      eventStateRef.current = { ...eventStateRef.current, isStreaming: false, error: message, activityStatus: null }
    }

    try {
      let response: Response | null = null
      let failure: { message: string; code: string | null } | null = null
      try {
        response = await post()
      } catch (err) {
        // Stop, and anything that is not the network, go to the catch below.
        if (!isNetworkError(err)) throw err
        failure = { message: (err as Error).message, code: null }
      }

      // The send never reached goapi (a release restarting it): wait for the
      // server as a dropped stream does, then follow the turn if the send
      // started one before goapi went, or send it once more if not.
      let isReplay = false
      if (response === null || isAwayHttpStatus(response.status)) {
        if (response) failure = await sendFailure(response)
        const status = await statusAfterDrop(sid)
        if (status === 'stopped') return
        if (status === 'away' || status === 'gone') {
          failTurn(status === 'away' ? SERVER_AWAY_MESSAGE : failure!.message, null)
          return
        }
        if (status.activeQuery) {
          const replay = await attemptReconnect(sid, status.activeQuery.queryId)
          if (!replay) {
            // A turn is running that this page cannot follow: draw what the
            // server has saved of it. The queue waits for the server.
            if (sessionIdRef.current === sid) await loadPersistedState(sid)
            setIsStreaming(false)
            showActivity(null)
            stopStuckDetection()
            eventStateRef.current = { ...eventStateRef.current, isStreaming: false }
            return
          }
          rewindToTurnBase()
          response = replay
          isReplay = true
        } else {
          // Once only: a second failure is shown as it is.
          showActivity(null)
          response = await post()
        }
      }

      if (!response.ok) {
        const { message, code } = await sendFailure(response)
        failTurn(message, code)
        // Refused, so nothing is running on its account.
        settled = true
        return
      }

      settled = await readSSEStream(response, 0, sid, isReplay)

      setIsStreaming(false)
      setActivityStatus(null)
      stopStuckDetection()
      eventStateRef.current = { ...eventStateRef.current, isStreaming: false, activityStatus: null }

      if (sessionIdRef.current) loadArtifacts(sessionIdRef.current)
    } catch (err: unknown) {
      if ((err as Error).name !== 'AbortError') {
        setError(err instanceof Error ? err.message : 'Unknown error')
        setErrorCode(null)
      }
      setIsStreaming(false)
      setActivityStatus(null)
      stopStuckDetection()
      eventStateRef.current = { ...eventStateRef.current, isStreaming: false, activityStatus: null }

      if (sessionIdRef.current) loadArtifacts(sessionIdRef.current)
    } finally {
      if (turnSeqRef.current === turn) {
        turnActiveRef.current = false
        // A turn the reader stopped (Stop, Nudge, leaving the session) is not
        // drained here: whoever stopped it decides what runs next.
        if (!abortController.signal.aborted) void drainQueue(sid, settled)
      }
    }
  }

  /**
   * Send a message. While a turn is running the message is QUEUED rather than
   * sent — POSTing a prompt mid-turn aborts the running turn in the sandbox.
   * It is drawn at once, below the running turn with a "Queued" marker, and
   * sent when that turn's stream ends (query_complete, an error, or Stop).
   * Several queued messages go one turn at a time, oldest first.
   */
  const sendMessage = useCallback(async (content: string, model?: string, attachmentIds?: string[], effort?: string, page?: PageSend) => {
    if (!session) return
    if (turnActiveRef.current) {
      const id = crypto.randomUUID()
      queueRef.current.push({ id, content, model, attachmentIds, effort, page })
      handleSSEEvent({ type: 'user_message_queued', data: { id, content }, timestamp: new Date().toISOString() })
      return
    }
    await runTurn(session.id, content, model, attachmentIds, undefined, effort, page)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, handleSSEEvent, loadArtifacts])

  const postPageToolResult = useCallback(async (callId: string, result: { ok: boolean; result?: unknown; error?: string }) => {
    const sid = sessionIdRef.current
    if (!sid) throw new Error('No session')
    const response = await apiFetch(endpoints.pageToolResult(sid), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ callId, ok: result.ok, ...(result.result !== undefined && { result: result.result }), ...(result.error && { error: result.error }) }),
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setPageCallWaiting = useCallback((waiting: boolean) => {
    pageCallWaitingRef.current = waiting
    if (!waiting) {
      // The clock restarts from the answer, not from the last event before the wait.
      lastEventTimeRef.current = Date.now()
      lastHeartbeatRef.current = Date.now()
    }
  }, [])

  const cancelSession = useCallback(async () => {
    if (!session) return
    // Read before the abort: the aborted send clears turnActiveRef as it
    // unwinds. A Chief of Staff turn can look finished on this page while its
    // sub-agents or workflows still run, so those count too.
    const state = eventStateRef.current
    const settle = turnActiveRef.current || state.isStreaming || [...state.toolCalls.values()].some(tc =>
      tc.subagent?.status === 'running' || tc.workflow?.status === 'running')
      || state.messages.some(m => m.toolCalls?.some(tc => tc.subagent?.status === 'running' || tc.workflow?.status === 'running'))
    abortControllerRef.current?.abort()
    try {
      await apiFetch(endpoints.cancel(session.id), { method: 'POST' })
    } catch {
      // Cancel POST is fire-and-forget
    }
    // The stream was cut above, so the server's own query_complete
    // ('cancelled') never reaches this page. Settle the turn as that event
    // would (the reducer's one branch, shared with replay): running tools are
    // cancelled, and so are sub-agents and workflows (Chief of Staff).
    if (settle) {
      handleSSEEvent({ type: 'query_complete', data: { status: 'cancelled' }, timestamp: new Date().toISOString() })
    }
    setIsStreaming(false)
    setActivityStatus(null)
    stopStuckDetection()
    eventStateRef.current = { ...eventStateRef.current, isStreaming: false, activityStatus: null }
    // Stop supersedes the running turn; anything queued goes next — only now,
    // after the cancel has been delivered, so it cannot be cancelled too.
    turnSeqRef.current++
    turnActiveRef.current = false
    void drainQueue(session.id, true)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  const deleteSession = useCallback(async () => {
    if (!session) return
    abortControllerRef.current?.abort()
    clearQueue()
    try {
      await apiFetch(endpoints.deleteSession(session.id), { method: 'DELETE' })
      resetEventState()
      setSession(null)
      setMessages([])
      setIsStreaming(false)
    } catch {
      // Silently handle delete errors
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session])

  function startContainerStatePoll(sessionId: string) {
    stopContainerStatePoll()
    _containerPollSessionId = sessionId

    const poll = async () => {
      if (_containerPollSessionId !== sessionId) return
      try {
        const resp = await apiFetch(endpoints.status(sessionId), { signal: AbortSignal.timeout(3000) })
        if (!resp.ok) return
        const data = await resp.json() as { sandboxState?: string }
        const state = data.sandboxState
        setSessionList(prev => {
          const idx = prev.findIndex(s => s.id === sessionId)
          if (idx === -1) return prev
          const row = prev[idx]
          const nextState = state && state !== 'unknown' ? state : row.container_state
          if (row.container_state === nextState) return prev
          const next = [...prev]
          next[idx] = { ...row, container_state: nextState }
          return next
        })
        if (containerPollSettled(state, turnActiveRef.current)) {
          stopContainerStatePoll()
        }
      } catch {
        // Silently ignore poll failures
      }
    }

    poll()
    _containerPollTimer = setInterval(poll, CONTAINER_POLL_MS)
  }

  function stopContainerStatePoll() {
    if (_containerPollTimer) {
      clearInterval(_containerPollTimer)
      _containerPollTimer = null
    }
    _containerPollSessionId = null
  }

  const clearSession = useCallback(() => {
    abortControllerRef.current?.abort()
    clearQueue()
    turnSeqRef.current++
    turnActiveRef.current = false
    resetEventState()
    setSession(null)
    setMessages([])
    setArtifacts([])
    setTodos([])
    setAskedQuestions(new Map())
    setIsStreaming(false)
    setError(null)
    setErrorCode(null)
    setActivityStatus(null)
    setPluginEvents([])
    stopStuckDetection()
    stopContainerStatePoll()
    sessionIdRef.current = null
  }, [])

  const resumeSession = useCallback(async (sessionId: string) => {
    clearQueue()
    turnSeqRef.current++
    turnActiveRef.current = false
    resetEventState()
    setError(null)
    setErrorCode(null)
    setMessages([])
    setArtifacts([])
    setAskedQuestions(new Map())
    try {
      // Load session metadata, events, messages and runtime status in parallel.
      // The status is needed BEFORE the history is drawn: it names the turn
      // that is still running, whose row must be left out (the reconnect
      // stream replays it from its first event).
      const [sessionData, eventsData, msgsData, initialStatus] = await Promise.all([
        apiGet<AgentSession>(endpoints.getSession(sessionId)),
        apiGet<{ events: QueryEventsRow[] }>(
          endpoints.queryEvents(sessionId)
        ).catch(() => ({ events: [] as QueryEventsRow[] })),
        apiGet<{ messages: PersistedAgentMessage[]; total: number }>(
          `${endpoints.messages(sessionId)}?limit=500`
        ),
        checkSessionStatus(sessionId),
      ])
      const activeQueryId = initialStatus?.activeQuery?.queryId || null

      const s = sessionData
      const workflowId = s.workflowId || (s as unknown as Record<string, string>).workflow_id || 'agent'
      const metadata = (s as unknown as Record<string, unknown>).metadata as Record<string, unknown> | undefined
      const sessionModel = (metadata?.model as string) || defaultModel || selectedModelRef.current
      const resumed: AgentSession = {
        id: s.id,
        status: (s.status as AgentSession['status']) || 'active',
        workflowId,
        persona: s.persona,
        customer: s.customer,
        job: s.job,
        // Without this the whole job set is lost on every reload: the UI's
        // precedence is `sessionJobs ?? session.jobs ?? []`, and sessionJobs is
        // null until a session_jobs_changed event arrives — which never happens
        // for a session nobody has changed. Every tab would paint zero jobs.
        jobs: s.jobs,
        model: sessionModel,
        metadata,
      }
      setSelectedModel(sessionModel)

      // Restore state via event replay or legacy reconstruction
      const queryEvents = eventsData.events || []
      let restored: AgentEventState
      if (queryEvents.length > 0) {
        const allEvents = historyEventsWithoutActiveTurn(queryEvents, activeQueryId)
        restored = replayEvents(allEvents)
        // Restore plugin events from raw events so plugins can reconstruct their state.
        const typeSet = pluginEventTypesRef.current
        setPluginEvents(typeSet.size > 0 ? allEvents.filter(e => typeSet.has(e.type)) : [])
      } else {
        const persisted = msgsData.messages || []
        restored = replayFromPersistedMessages(persisted)
        // Plugin state cannot be reconstructed from persisted messages — falls back to empty.
        // Plugins will render nothing until new live events arrive (acceptable).
        setPluginEvents([])
      }

      eventStateRef.current = {
        ...eventStateRef.current,
        messages: restored.messages,
        askedQuestions: restored.askedQuestions,
        renderedTables: restored.renderedTables,
        renderedCharts: restored.renderedCharts,
        createdDashboards: restored.createdDashboards,
        todos: restored.todos,
        sessionInfo: restored.sessionInfo,
        subagentEvents: restored.subagentEvents,
        artifacts: restored.artifacts,
        installedSkills: restored.installedSkills,
        sessionJobs: restored.sessionJobs,
      }
      setAskedQuestions(restored.askedQuestions)
      setRenderedTables(restored.renderedTables)
      setRenderedCharts(restored.renderedCharts)
      setCreatedDashboards(restored.createdDashboards)
      setTodos(restored.todos)
      setArtifacts(restored.artifacts)
      setSessionJobs(restored.sessionJobs)

      setSession(resumed)
      sessionIdRef.current = resumed.id
      setMessages(restored.messages)

      startContainerStatePoll(sessionId)
      loadArtifacts(sessionId)

      // Nothing is restored: the chat's process starts on its next message.
      const activeQuery = initialStatus?.activeQuery ?? null

      // A turn is still running: reattach to it. Gated on activeQuery being
      // present — NOT on the session's status string, which the backend
      // reports as `running` while this used to check `active|streaming`, so
      // a reload mid-turn never reconnected (#1346 item 1).
      if (activeQuery?.queryId && sessionIdRef.current === sessionId) {
        eventStateRef.current = { ...eventStateRef.current, isStreaming: true }
        setIsStreaming(true)
        setActivityStatus({ label: 'Reconnecting...', category: 'system' })
        // The history without the active turn (plus its user prompt) is the
        // base the replay builds on.
        markTurnBase()

        const abortController = new AbortController()
        abortControllerRef.current = abortController
        startStuckDetection()
        // A reattached turn is a running turn: messages sent now are queued.
        const turn = ++turnSeqRef.current
        turnActiveRef.current = true
        let settled = false

        try {
          const reconnectResp = await attemptReconnect(sessionId, activeQuery.queryId)
          if (reconnectResp) {
            rewindToTurnBase()
            try {
              settled = await readSSEStream(reconnectResp, 0, sessionId, true)
            } catch (err) {
              // Stop aborts the stream; that is the reader's choice, not an error.
              if ((err as Error).name !== 'AbortError') throw err
            }
          } else if (sessionIdRef.current === sessionId) {
            // No stream to read: draw everything the server has persisted,
            // the active turn's row included, rather than leave it out.
            await loadPersistedState(sessionId)
          }
        } finally {
          if (turnSeqRef.current === turn) {
            turnActiveRef.current = false
            if (!abortController.signal.aborted) void drainQueue(sessionId, settled)
          }
        }

        setIsStreaming(false)
        setActivityStatus(null)
        stopStuckDetection()
        eventStateRef.current = { ...eventStateRef.current, isStreaming: false, activityStatus: null }
        if (sessionIdRef.current) loadArtifacts(sessionIdRef.current)
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unknown error')
      setErrorCode(null)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadArtifacts, handleSSEEvent])

  const SESSION_PAGE_SIZE = 20

  const listSessions = useCallback(async (customer?: string, userEmail?: string, page?: string) => {
    try {
      const params = new URLSearchParams({ limit: String(SESSION_PAGE_SIZE) })
      if (customer) params.set('customer', customer)
      if (userEmail) params.set('user_email', userEmail)
      if (page) params.set('page', page)
      params.set('exclude_workflow_prefix', 'assistant-')
      const data = await apiGet<{ sessions: AgentSessionListItem[] }>(
        `${endpoints.listSessions}?${params.toString()}`
      )
      const freshSessions = data.sessions || []

      setSessionList(prev => {
        if (prev.length <= SESSION_PAGE_SIZE) {
          setHasMoreSessions(freshSessions.length >= SESSION_PAGE_SIZE)
          return freshSessions
        }
        const freshMap = new Map(freshSessions.map(s => [s.id, s]))
        const updated = prev.map(s => freshMap.get(s.id) ?? s)
        const existingIds = new Set(prev.map(s => s.id))
        const newSessions = freshSessions.filter(s => !existingIds.has(s.id))
        return newSessions.length > 0 ? [...newSessions, ...updated] : updated
      })
    } catch (err) {
      console.error('Failed to list sessions:', err)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authHeader, getAuthToken])

  const sessionListRef = useRef(sessionList)
  sessionListRef.current = sessionList

  const loadMoreSessions = useCallback(async (customer?: string, userEmail?: string, page?: string) => {
    try {
      const params = new URLSearchParams({
        has_messages: 'true',
        limit: String(SESSION_PAGE_SIZE),
        offset: String(sessionListRef.current.length),
      })
      if (customer) params.set('customer', customer)
      if (userEmail) params.set('user_email', userEmail)
      if (page) params.set('page', page)
      params.set('exclude_workflow_prefix', 'assistant-')
      const data = await apiGet<{ sessions: AgentSessionListItem[] }>(
        `${endpoints.listSessions}?${params.toString()}`
      )
      const newSessions = data.sessions || []
      setSessionList(prev => [...prev, ...newSessions])
      setHasMoreSessions(newSessions.length >= SESSION_PAGE_SIZE)
    } catch (err) {
      console.error('Failed to load more sessions:', err)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authHeader, getAuthToken])

  const searchSessions = useCallback(async (customer: string, query: string, userEmail?: string): Promise<AgentMessageSearchResult[]> => {
    try {
      const params = new URLSearchParams({ q: query, customer })
      if (userEmail) params.set('user_email', userEmail)
      const data = await apiGet<{ results: AgentMessageSearchResult[] }>(
        `${endpoints.searchMessages}?${params.toString()}`
      )
      return data.results || []
    } catch (err) {
      console.error('Failed to search sessions:', err)
      return []
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBaseUrl, authHeader, getAuthToken])

  const nudgeAgent = useCallback(async () => {
    if (!session) return
    abortControllerRef.current?.abort()
    try {
      await apiFetch(endpoints.cancel(session.id), { method: 'POST' })
    } catch { /* ignore */ }
    setIsStreaming(false)
    setActivityStatus(null)
    stopStuckDetection()
    eventStateRef.current = { ...eventStateRef.current, isStreaming: false, activityStatus: null }
    // The nudge replaces the stuck turn directly — it must not wait in the
    // queue behind it. Anything already queued follows the nudge's turn.
    turnSeqRef.current++
    turnActiveRef.current = false
    await runTurn(session.id, 'Please continue where you left off.')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, sendMessage])

  // The host's door into the job set (see setSessionJobs on the interface).
  // Writes BOTH the ref and the React state so the next reducer pass compares
  // against what is actually on screen — otherwise a later session_jobs_changed
  // carrying the same set would look unchanged and be skipped.
  const adoptSessionJobs = useCallback((jobs: string[]) => {
    eventStateRef.current = { ...eventStateRef.current, sessionJobs: jobs }
    setSessionJobs(jobs)
  }, [])

  return {
    session,
    messages,
    isStreaming,
    pluginEvents,
    isCreating,
    error,
    errorCode,
    activityStatus,
    artifacts,
    todos,
    renderedTables,
    renderedCharts,
    askedQuestions,
    createdDashboards,
    pendingPageToolRequest,
    pageToolCalls,
    pendingSettingsUpdate,
    allPersonas,
    sessionList,
    hasMoreSessions,
    toolInputBuffer,
    subagentEvents: eventStateRef.current.subagentEvents,
    installedSkills: eventStateRef.current.installedSkills,
    hoistedSkill: eventStateRef.current.hoistedSkill,
    sessionJobs,
    queuedMessages,
    setSessionJobs: adoptSessionJobs,
    selectedModel,
    setSelectedModel,
    lastHeartbeat: lastHeartbeatRef.current,
    createSession,
    sendMessage,
    postPageToolResult,
    setPageCallWaiting,
    cancelSession,
    deleteSession,
    loadPersonas,
    resumeSession,
    listSessions,
    loadMoreSessions,
    searchSessions,
    clearSession,
    markQuestionAnswered,
    clearPendingPageToolRequest,
    clearPendingSettingsUpdate,
    stuckStatus,
    nudgeAgent,
  }
}
